import { useCallback, useEffect, useState } from 'react';

/** The non-standard event Chromium fires when the app can be installed. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export interface InstallPromptState {
  /** A native install prompt was captured and can be shown. */
  canInstall: boolean;
  /** iOS Safari has no prompt event: show the "Share → Add to Home Screen" hint. */
  showIosHint: boolean;
  /** Already running as an installed app. */
  isStandalone: boolean;
  promptInstall: () => Promise<InstallOutcome>;
}

let capturedPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<
  (event: BeforeInstallPromptEvent | null) => void
>();
let captureStarted = false;

/**
 * Chromium fires `beforeinstallprompt` once, early. Call this at startup so a
 * screen that mounts later (Settings) still receives the event.
 */
export function captureInstallPrompt(): void {
  if (captureStarted || typeof window === 'undefined') {
    return;
  }
  captureStarted = true;
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    capturedPrompt = event as BeforeInstallPromptEvent;
    promptListeners.forEach(listener => listener(capturedPrompt));
  });
  window.addEventListener('appinstalled', () => {
    capturedPrompt = null;
    promptListeners.forEach(listener => listener(null));
  });
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) {
    return true;
  }
  // iPadOS 13+ reports itself as a Mac with touch support.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}

export function isStandaloneDisplay(): boolean {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches
    ) {
      return true;
    }
    return (
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/**
 * Captures `beforeinstallprompt` (Chromium) and reports the iOS Safari case.
 * Hidden entirely once the app runs standalone.
 */
export function useInstallPrompt(): InstallPromptState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    capturedPrompt
  );
  const [isStandalone, setIsStandalone] = useState(isStandaloneDisplay);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    promptListeners.add(setDeferred);
    setDeferred(capturedPrompt);
    window.addEventListener('appinstalled', onInstalled);

    let media: MediaQueryList | null = null;
    const onDisplayModeChange = () => setIsStandalone(isStandaloneDisplay());
    try {
      media = window.matchMedia('(display-mode: standalone)');
      media.addEventListener?.('change', onDisplayModeChange);
    } catch {
      media = null;
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      promptListeners.delete(setDeferred);
      window.removeEventListener('appinstalled', onInstalled);
      media?.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    if (!deferred) {
      return 'unavailable';
    }
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // The event can be used once, whatever the answer was.
      capturedPrompt = null;
      setDeferred(null);
      return choice.outcome;
    } catch {
      setDeferred(null);
      return 'unavailable';
    }
  }, [deferred]);

  const standalone = isStandalone;
  const showIosHint =
    !standalone &&
    isIosDevice() &&
    (navigator as Navigator & { standalone?: boolean }).standalone === false;

  return {
    canInstall: !standalone && deferred !== null,
    showIosHint,
    isStandalone: standalone,
    promptInstall,
  };
}
